import { z } from 'zod';
/**
 * Workflow Structure Validation
 *
 * Single source of truth for validating workflow **structure** — the minimum
 * shape that the editor and runtime assume is always present and correctly
 * formed. Intentionally separate from activation/publish validation
 * (WorkflowValidationService) which checks semantic correctness (trigger
 * presence, known node types, credential issues, etc.).
 *
 * Lives in n8n-workflow so it can be shared by:
 *   - Backend: create, import, and structural updates reject malformed payloads (400).
 *     Metadata-only updates skip the check so legacy data stays editable.
 *   - Frontend: open path warns but still renders; the sample-template JSON route blocks
 *
 * Validates:
 *   - Required node fields (name, type, position)
 *   - Position is a 2-number tuple
 *   - Connection entries have valid node/type/index
 *   - No duplicate node names
 *   - No duplicate node ids, where present
 *   - Connection source/target keys reference existing nodes
 *
 * Does NOT validate:
 *   - Whether a node type is installed
 *   - Node parameter correctness
 *   - Credential validity
 *   - Activation readiness
 *
 */
const workflowNodeStructureSchema = z
    .object({
    name: z.string().min(1),
    type: z.string().min(1),
    position: z.tuple([z.number(), z.number()]),
    parameters: z.record(z.string(), z.unknown()).optional(),
    id: z.string().optional(),
    typeVersion: z.number().optional(),
    disabled: z.boolean().optional(),
})
    .passthrough();
const connectionEntrySchema = z
    .object({
    node: z.string().min(1),
    type: z.string().min(1),
    index: z.number().int().min(0),
})
    .passthrough();
// Buckets can be null when a multi-output node has unused output slots.
// Matches NodeInputConnections type.
const connectionBucketSchema = z.array(connectionEntrySchema).nullable().optional();
const workflowConnectionsStructureSchema = z.record(z.string(), z.record(z.string(), z.array(connectionBucketSchema)));
const workflowStructureSchema = z.object({
    nodes: z.array(workflowNodeStructureSchema),
    connections: workflowConnectionsStructureSchema,
});
export const formatWorkflowStructureIssuePath = (path) => {
    if (path.length === 0)
        return 'workflow';
    return path.reduce((acc, segment) => {
        if (typeof segment === 'number')
            return `${acc}[${segment}]`;
        return acc ? `${acc}.${segment}` : segment;
    }, '');
};
const formatIssuesDescription = (issues) => issues
    .map(({ path, message }) => `${formatWorkflowStructureIssuePath(path)}: ${message}`)
    .join('\n');
export class WorkflowStructureValidationError extends Error {
    issues;
    name = 'WorkflowStructureValidationError';
    description;
    constructor(issues) {
        super('Invalid workflow structure');
        this.issues = issues;
        this.description = formatIssuesDescription(issues);
    }
}
export function safeParseWorkflowStructure(input) {
    const parsed = workflowStructureSchema.safeParse(input);
    if (!parsed.success) {
        return {
            success: false,
            issues: parsed.error.issues,
        };
    }
    const { nodes, connections } = parsed.data;
    const issues = [];
    const nodeNames = new Set();
    const nodeNameById = new Map();
    for (const [index, node] of nodes.entries()) {
        // Ids are optional in the schema because the backend fills in absent and empty ones on
        // save. A supplied id must be unique: it identifies a node independently of its name, and
        // per-node state keyed on it would otherwise be shared between two nodes.
        if (node.id !== undefined && node.id !== '') {
            const firstNodeName = nodeNameById.get(node.id);
            if (firstNodeName === undefined)
                nodeNameById.set(node.id, node.name);
            else
                issues.push({
                    path: ['nodes', index, 'id'],
                    message: `Nodes "${firstNodeName}" and "${node.name}" share the node ID "${node.id}"`,
                    code: 'duplicate_node_id',
                });
        }
        if (nodeNames.has(node.name))
            issues.push({
                path: ['nodes', index, 'name'],
                message: `Duplicate node name "${node.name}"`,
                code: 'duplicate_node_name',
            });
        else
            nodeNames.add(node.name);
    }
    for (const sourceNodeName of Object.keys(connections)) {
        if (!nodeNames.has(sourceNodeName)) {
            issues.push({
                path: ['connections', sourceNodeName],
                message: `Connection source "${sourceNodeName}" does not reference an existing node`,
                code: 'unknown_connection_source',
            });
        }
        const connectionTypes = connections[sourceNodeName];
        for (const connectionType of Object.keys(connectionTypes)) {
            const buckets = connectionTypes[connectionType];
            for (const [sourceIndex, bucket] of buckets.entries()) {
                for (const [targetIndex, connection] of (bucket ?? []).entries()) {
                    if (!nodeNames.has(connection.node)) {
                        issues.push({
                            path: [
                                'connections',
                                sourceNodeName,
                                connectionType,
                                sourceIndex,
                                targetIndex,
                                'node',
                            ],
                            message: `Connection target "${connection.node}" does not reference an existing node`,
                            code: 'unknown_connection_target',
                        });
                    }
                }
            }
        }
    }
    if (issues.length > 0) {
        return { success: false, issues };
    }
    return {
        success: true,
        data: parsed.data,
    };
}
export function parseWorkflowStructure(input) {
    const result = safeParseWorkflowStructure(input);
    if (!result.success) {
        throw new WorkflowStructureValidationError(result.issues);
    }
    return result.data;
}
//# sourceMappingURL=workflow-structure-validation.js.map